import {
    faChevronDown,
    faCircleMinus,
    faCirclePlus,
    faCat,
    faCircleExclamation,
    faDog,
    faTurtle,
    IconDefinition
} from '@fortawesome/pro-regular-svg-icons';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { saveActivityThunk } from '@spike/activities-action';
import {
    Activity,
    ActivityType,
    FeedingAte,
    HydrationIntake,
    ReliefBucket,
    ReliefCategory,
    RestValue
} from '@spike/activity-model';
import { Period, PetTypeIds } from '@spike/model';
import Pet from '@spike/pet-model';
import { StaffStatus } from 'reducers/staff/StaffState';
import styles from 'components/Activities/Form/ActivityForm.module.scss';
import {
    ACTIVITY_CREATE_OPTIONS,
    ACTIVITY_FORM_CARD_COLORS_MAP,
    ACTIVITY_TITLE_MAP,
    HYDRATION_LABELS,
    RELIEF_SLOTS,
    REST_LABELS
} from 'components/Activities/Icon/ActivityIcon';
import AlertMessage from 'components/UI/V3/AlertMessage';
import Button from 'components/UI/V3/Button/Button';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { Checkbox } from 'components/UI/V3/Forms/Checkbox/Checkbox';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { RadioGroup } from 'components/UI/V3/Forms/RadioGroup/RadioGroup';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import { Select } from 'components/UI/V3/Forms/Select/Select';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import EditableCounterField from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/EditableCounterField';
import {
    getMaxDecimalsForUnit,
    getStepForUnit,
    isValidDecimalPlaces
} from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/MedicationDosage/medicationDosageUnitsConfig';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import { TimeSelector } from 'components/UI/V3/Forms/TimeSelector/TimeSelector';
import { IconCard } from 'components/UI/V3/IconCard/IconCard';
import MultiSelectField, { MultiSelectFieldOption } from 'components/UI/V3/MultiSelectField';
import PhotosSelector from 'components/UI/V3/PhotosSelector';
import { PhotoWrapper } from 'components/UI/V3/PhotosSelector/PhotosSelector';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import TagsSelector from 'components/UI/V3/TagsSelector/TagsSelector';
import VideosSelector from 'components/UI/V3/VideosSelector';
import { VideoWrapper } from 'components/UI/V3/VideosSelector/VideosSelector';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import clsx from 'clsx';
import moment, { Moment } from 'moment-timezone';
import Staff from 'model/Staff';
import React from 'react';
import { useSelector } from 'react-redux';
import { dispatch, RootState } from 'store';
import { v4 as uuid } from 'uuid';

interface ActivityFormProps {
    customerId: number;
    customerName?: string;
    bookingId?: number;
    type?: ActivityType;
    pets: Array<Pet>;
    petIds?: Array<number>;
    activityToEdit?: Activity;
    dateLimits?: Period;
    allowedDates?: Array<string>;
    availableDateTimeRanges?: Record<string, Array<Period>>;
    zIndex?: number;
    onClose?: (_activity?: Activity) => void;
}

const isDateAllowed = (date: Moment, allowedDates?: Array<string>): boolean => {
    if (!allowedDates || allowedDates.length === 0) {
        return true;
    }

    return allowedDates.includes(date.format('YYYY-MM-DD'));
};

const getInitialBaseDate = (timeZone: string, dateLimits?: Period, allowedDates?: Array<string>): Moment => {
    const now = moment().tz(timeZone);

    if (allowedDates && allowedDates.length > 0) {
        if (isDateAllowed(now, allowedDates)) {
            return now;
        }

        return moment.tz(allowedDates[0], 'YYYY-MM-DD', timeZone);
    }

    if (dateLimits?.from && dateLimits?.to) {
        if (now.isBefore(dateLimits.from, 'day') || now.isAfter(dateLimits.to, 'day')) {
            return dateLimits.from.clone();
        }
    }

    return now;
};

const getTimeOptionsForDateRange = (
    selectedDate: Moment | null,
    dateLimits?: Period,
    availableDateTimeRanges?: Record<string, Array<Period>>
): Array<{ id: string; label: string }> => {
    const options: { id: string; label: string }[] = [];

    if (!selectedDate) {
        return options;
    }

    const selectedDateKey = selectedDate.format('YYYY-MM-DD');
    const dateTimeRanges = availableDateTimeRanges?.[selectedDateKey];

    if (dateTimeRanges && dateTimeRanges.length > 0) {
        for (let hour = 0; hour <= 23; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const time = selectedDate.clone().set({
                    hour,
                    minute,
                    second: 0,
                    millisecond: 0
                });
                const isWithinRanges = dateTimeRanges.some(range => {
                    return time.isBetween(range.from, range.to, 'minute', '[]');
                });

                if (!isWithinRanges) {
                    continue;
                }

                const formattedTime = time.format('hh:mma');

                if (!options.some(option => option.id === formattedTime)) {
                    options.push({
                        id: formattedTime,
                        label: formattedTime
                    });
                }
            }
        }

        return options;
    }

    let startHour = 0;
    let startMinute = 0;
    let endHour = 23;
    let endMinute = 59;

    if (dateLimits?.from && dateLimits?.to) {
        const selectedDateFormatted = selectedDate.format('YYYY-MM-DD');
        const fromDateFormatted = dateLimits.from.format('YYYY-MM-DD');
        const toDateFormatted = dateLimits.to.format('YYYY-MM-DD');

        if (selectedDateFormatted === fromDateFormatted) {
            startHour = dateLimits.from.hour();
            startMinute = dateLimits.from.minute();
        }

        if (selectedDateFormatted === toDateFormatted) {
            endHour = dateLimits.to.hour();
            endMinute = dateLimits.to.minute();
        }

        if (selectedDate.isBefore(dateLimits.from, 'day') || selectedDate.isAfter(dateLimits.to, 'day')) {
            return options;
        }
    }

    for (let hour = 0; hour <= 23; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            const isWithinStart = hour > startHour || (hour === startHour && minute >= startMinute);
            const isWithinEnd = hour < endHour || (hour === endHour && minute <= endMinute);

            if (isWithinStart && isWithinEnd) {
                const time = moment().hour(hour).minute(minute).second(0);
                const formattedTime = time.format('hh:mma');

                options.push({
                    id: formattedTime,
                    label: formattedTime
                });
            }
        }
    }

    return options;
};

const getInitialDateWithValidTime = (
    baseDate: Moment,
    options: Array<{ id: string; label: string }>,
    dateLimits?: Period
): Moment => {
    if (options.length === 0) {
        return baseDate.clone();
    }

    const currentTime = baseDate.format('hh:mma');

    const currentTimeOption = options.find(option => option.id === currentTime);

    if (currentTimeOption) {
        return baseDate.clone().set({
            hour: moment(currentTimeOption.id, 'hh:mma').hour(),
            minute: moment(currentTimeOption.id, 'hh:mma').minute()
        });
    }

    const currentTimeMoment = moment(currentTime, 'hh:mma');
    let closestOption = options[0];
    let smallestDiff = Infinity;

    for (const option of options) {
        const optionTime = moment(option.id, 'hh:mma');
        const diff = Math.abs(optionTime.diff(currentTimeMoment, 'minutes'));

        if (diff < smallestDiff) {
            smallestDiff = diff;
            closestOption = option;
        }
    }

    if (dateLimits?.from && dateLimits?.to) {
        if (baseDate.isBefore(dateLimits.from, 'day') || baseDate.isAfter(dateLimits.to, 'day')) {
            const startDate = dateLimits.from.clone();
            return startDate.set({
                hour: moment(options[0].id, 'hh:mma').hour(),
                minute: moment(options[0].id, 'hh:mma').minute()
            });
        }
    }

    return baseDate.clone().set({
        hour: moment(closestOption.id, 'hh:mma').hour(),
        minute: moment(closestOption.id, 'hh:mma').minute()
    });
};

const toRadioOptions = <T extends string>(labels: Record<T, string>): Array<{ value: T; inlineLabel: string }> =>
    (Object.keys(labels) as Array<T>).map(value => ({ value, inlineLabel: labels[value] }));

const HYDRATION_OPTIONS = toRadioOptions(HYDRATION_LABELS);
const REST_OPTIONS = toRadioOptions(REST_LABELS);

interface ReliefSlotState {
    selected: boolean;
    count: number;
}

interface ReliefCategoryState {
    enabled: boolean;
    morning: ReliefSlotState;
    afternoon: ReliefSlotState;
    evening: ReliefSlotState;
    note: string;
}

// A non-null count (including 0) means the checkbox is selected; null means
// the slot was left unchecked for this category.
const slotFromValue = (value: number | null | undefined): ReliefSlotState =>
    value === null || value === undefined ? { selected: false, count: 0 } : { selected: true, count: value };

const slotToValue = (slot: ReliefSlotState): number | null => (slot.selected ? slot.count : null);

const emptyReliefCategory = (): ReliefCategoryState => ({
    enabled: false,
    morning: { selected: true, count: 0 },
    afternoon: { selected: true, count: 0 },
    evening: { selected: true, count: 0 },
    note: ''
});

const reliefCategoryFromBuckets = (
    buckets: Array<ReliefBucket> | undefined,
    type: ReliefCategory
): ReliefCategoryState => {
    const bucket = buckets?.find(item => item.type === type);

    if (!bucket) {
        return emptyReliefCategory();
    }

    return {
        enabled: true,
        morning: slotFromValue(bucket.morning),
        afternoon: slotFromValue(bucket.afternoon),
        evening: slotFromValue(bucket.evening),
        note: bucket.note ?? ''
    };
};

const reliefCategorySelected = (category: ReliefCategoryState): boolean =>
    category.morning.selected || category.afternoon.selected || category.evening.selected;

const reliefBucketFromCategory = (type: ReliefCategory, category: ReliefCategoryState): ReliefBucket => ({
    type,
    morning: slotToValue(category.morning),
    afternoon: slotToValue(category.afternoon),
    evening: slotToValue(category.evening),
    ...(category.note.trim() ? { note: category.note.trim() } : {})
});

const Stepper: React.FC<{ value: number; disabled?: boolean; onChange: (_value: number) => void }> = ({
    value,
    disabled,
    onChange
}) => (
    <div className={styles.stepper}>
        <button
            type="button"
            className={styles.stepperButton}
            disabled={disabled}
            onClick={() => onChange(Math.max(0, value - 1))}
        >
            <FontAwesomeIcon icon={faCircleMinus} />
        </button>
        <span className={styles.stepperValue}>{value}</span>
        <button
            type="button"
            className={styles.stepperButton}
            disabled={disabled}
            onClick={() => onChange(value + 1)}
        >
            <FontAwesomeIcon icon={faCirclePlus} />
        </button>
    </div>
);

const ReliefSection: React.FC<{
    title: string;
    noteLabel: string;
    value: ReliefCategoryState;
    onChange: (_value: ReliefCategoryState) => void;
}> = ({ title, noteLabel, value, onChange }) => (
    <div className={clsx(styles.reliefSection, { [styles.reliefSectionCollapsed]: !value.enabled })}>
        <div className={styles.reliefHeader}>
            <label className={styles.reliefSwitch}>
                <Switch
                    checked={value.enabled}
                    onChange={enabled => onChange({ ...value, enabled })}
                />
                <span className={styles.reliefTitle}>{title}</span>
            </label>
            {value.enabled && <span className={styles.reliefHint}>How Many Times?</span>}
        </div>

        {value.enabled && (
            <>
                {RELIEF_SLOTS.map(slot => {
                    const slotState = value[slot.key];

                    return (
                        <div
                            key={slot.key}
                            className={styles.reliefSlot}
                        >
                            <Checkbox
                                label={slot.label}
                                className={styles.reliefSlotCheckbox}
                                checked={slotState.selected}
                                onChange={selected => onChange({ ...value, [slot.key]: { ...slotState, selected } })}
                            />
                            <Stepper
                                value={slotState.count}
                                disabled={!slotState.selected}
                                onChange={count => onChange({ ...value, [slot.key]: { ...slotState, count } })}
                            />
                        </div>
                    );
                })}

                <Textarea
                    value={value.note}
                    maxLength={500}
                    label={noteLabel}
                    placeholder="Notes here..."
                    onChange={e => onChange({ ...value, note: e.target.value })}
                />
            </>
        )}
    </div>
);

export const ActivityForm: React.FC<ActivityFormProps> = ({
    pets,
    type,
    bookingId,
    customerName,
    activityToEdit,
    petIds,
    dateLimits,
    allowedDates,
    availableDateTimeRanges,
    zIndex,
    onClose
}) => {
    const timeZone = useTimeZone();
    const apiClientWrapper = useApiClientWrapper();

    const initialBaseDate = getInitialBaseDate(timeZone, dateLimits, allowedDates);
    const fallbackMinDate =
        allowedDates && allowedDates.length > 0
            ? moment.tz(allowedDates[0], 'YYYY-MM-DD', timeZone)
            : initialBaseDate.clone().subtract(1, 'year');
    const fallbackMaxDate =
        allowedDates && allowedDates.length > 0
            ? moment.tz(allowedDates[allowedDates.length - 1], 'YYYY-MM-DD', timeZone)
            : initialBaseDate;
    const initialTimeOptions = getTimeOptionsForDateRange(initialBaseDate, dateLimits, availableDateTimeRanges);
    const initialDate = getInitialDateWithValidTime(initialBaseDate, initialTimeOptions, dateLimits);

    const isLoading = useSelector((state: RootState) => state.activities.loading);
    const marketplaceId = useSelector((state: RootState) => state.marketplace.marketplace.id);
    const masterDataActivitySubtypes = useSelector((state: RootState) => state.masterData.data.activitySubtypes);
    const feedingAteValues = useSelector((state: RootState) => state.masterData.data.feedingAteValues);
    const medicationMethodTypes = useSelector((state: RootState) => state.masterData.data.medicationMethodTypes);
    const medicationDosageUnits = useSelector((state: RootState) => state.masterData.data.medicationDosageUnits);
    const staffMembers = useSelector((state: RootState) => state.staff.staff);
    const staffStatus = useSelector((state: RootState) => state.staff.status);

    const reliefBuckets =
        activityToEdit?.type === ActivityType.RELIEFS
            ? (activityToEdit.activitySubtype as Array<ReliefBucket> | undefined)
            : undefined;

    const defaultValues = {
        type: (activityToEdit?.type || type || null) as ActivityType | null,
        date: activityToEdit?.date || initialDate,
        note: activityToEdit?.note || '',
        petIds: activityToEdit?.pets.map(pet => pet.id) || petIds || [],
        incidentSubtypeIds: activityToEdit?.type === ActivityType.INCIDENT ? activityToEdit.activitySubtype || [] : [],
        incidentStaffIds: activityToEdit?.type === ActivityType.INCIDENT ? activityToEdit.involvedStaffIds || [] : [],
        enrichmentSubtypeIds:
            activityToEdit?.type === ActivityType.ENRICHMENT ? activityToEdit.activitySubtype || [] : [],
        enrichmentStaffIds:
            activityToEdit?.type === ActivityType.ENRICHMENT ? activityToEdit.involvedStaffIds || [] : [],
        photos:
            activityToEdit?.photos.map(photo => ({
                id: photo.id,
                url: photo.url,
                fileToUpload: photo.file,
                destroy: photo.destroy
            })) || [],
        videos:
            activityToEdit?.videos.map(video => ({
                id: video.id,
                url: video.url,
                fileToUpload: video.file,
                destroy: video.destroy
            })) || [],
        shared: activityToEdit?.shared ?? false,
        ate: activityToEdit?.type === ActivityType.FEEDING ? activityToEdit.ate ?? null : null,
        medicationName: activityToEdit?.type === ActivityType.MEDICATION ? activityToEdit.medicationName ?? '' : '',
        medicationMethodId:
            activityToEdit?.type === ActivityType.MEDICATION ? activityToEdit.medicationMethodId ?? null : null,
        dosageAmount: activityToEdit?.type === ActivityType.MEDICATION ? activityToEdit.dosageAmount ?? 0.5 : 0.5,
        dosageUnitId: activityToEdit?.type === ActivityType.MEDICATION ? activityToEdit.dosageUnitId ?? null : null,
        hydrationIntake:
            activityToEdit?.type === ActivityType.HYDRATION
                ? (activityToEdit.activitySubtype?.[0] as HydrationIntake) ?? null
                : null,
        restValue:
            activityToEdit?.type === ActivityType.REST
                ? (activityToEdit.activitySubtype?.[0] as RestValue) ?? null
                : null,
        peed: reliefCategoryFromBuckets(reliefBuckets, ReliefCategory.PEED),
        pooped: reliefCategoryFromBuckets(reliefBuckets, ReliefCategory.POOPED)
    };

    const [isDateSelectorExpanded, setIsDateSelectorExpanded] = React.useState(false);

    const [error, setError] = React.useState<string | null>(null);
    const [savedActivity, setSavedActivity] = React.useState<Activity | null>(null);

    const [selectedType, setSelectedType] = React.useState<ActivityType | null>(defaultValues.type);
    const [selectedPetIds, setSelectedPetIds] = React.useState<Array<number>>(defaultValues.petIds);
    const [selectedDate, setSelectedDate] = React.useState<Moment | null>(defaultValues.date);
    const [note, setNote] = React.useState<string>(defaultValues.note);
    const [selectedIncidentSubtypeIds, setSelectedIncidentSubtypeIds] = React.useState<Array<string>>(
        defaultValues.incidentSubtypeIds
    );
    const [selectedIncidentStaffIds, setSelectedIncidentStaffIds] = React.useState<Array<number>>(
        defaultValues.incidentStaffIds
    );
    const [selectedEnrichmentSubtypeIds, setSelectedEnrichmentSubtypeIds] = React.useState<Array<string>>(
        defaultValues.enrichmentSubtypeIds
    );
    const [selectedEnrichmentStaffIds, setSelectedEnrichmentStaffIds] = React.useState<Array<number>>(
        defaultValues.enrichmentStaffIds
    );

    // Attachments
    const [photos, setPhotos] = React.useState<Array<PhotoWrapper>>(defaultValues.photos);
    const [videos, setVideos] = React.useState<Array<VideoWrapper>>(defaultValues.videos);
    const [shared, setShared] = React.useState<boolean>(defaultValues.shared);

    // Feeding
    const [selectedAte, setSelectedAte] = React.useState<FeedingAte | null>(defaultValues.ate);

    // Medication
    const [medicationName, setMedicationName] = React.useState<string>(defaultValues.medicationName);
    const [selectedMethodId, setSelectedMethodId] = React.useState<number | null>(defaultValues.medicationMethodId);
    const [dosageAmount, setDosageAmount] = React.useState<number>(defaultValues.dosageAmount);
    const [selectedUnitId, setSelectedUnitId] = React.useState<number | null>(defaultValues.dosageUnitId);

    const [hydrationIntake, setHydrationIntake] = React.useState<HydrationIntake | null>(defaultValues.hydrationIntake);
    const [restValue, setRestValue] = React.useState<RestValue | null>(defaultValues.restValue);
    const [peed, setPeed] = React.useState<ReliefCategoryState>(defaultValues.peed);
    const [pooped, setPooped] = React.useState<ReliefCategoryState>(defaultValues.pooped);

    const title = React.useMemo(() => {
        const action = activityToEdit ? 'Edit' : 'Add';
        const activityType = (activityToEdit ? activityToEdit.type : selectedType) as ActivityType | null;
        const activityTypeTitle = activityType ? ACTIVITY_TITLE_MAP[activityType] ?? activityType : 'Activity';

        return `${action} ${activityTypeTitle}`;
    }, [activityToEdit, selectedType]);

    const incidentSubtypeOptions = React.useMemo<Array<MultiSelectFieldOption<string>>>(
        () =>
            (masterDataActivitySubtypes?.incident ?? []).map(option => ({
                id: option.id,
                label: option.name
            })),
        [masterDataActivitySubtypes]
    );

    const enrichmentSubtypeOptions = React.useMemo<Array<MultiSelectFieldOption<string>>>(
        () =>
            (masterDataActivitySubtypes?.enrichment ?? []).map(option => ({
                id: option.id,
                label: option.name
            })),
        [masterDataActivitySubtypes]
    );

    const timeOptions = React.useMemo(() => {
        return getTimeOptionsForDateRange(selectedDate, dateLimits, availableDateTimeRanges);
    }, [selectedDate, dateLimits, availableDateTimeRanges]);

    const medicationMethodOptions = React.useMemo(
        () => (medicationMethodTypes ?? []).map(m => ({ label: m.name, value: String(m.id) })),
        [medicationMethodTypes]
    );

    const medicationUnitOptions = React.useMemo(
        () => (medicationDosageUnits ?? []).map(u => ({ label: u.name, value: String(u.id) })),
        [medicationDosageUnits]
    );

    const selectedUnitName = React.useMemo(
        () => (medicationDosageUnits ?? []).find(u => u.id === selectedUnitId)?.name ?? '',
        [medicationDosageUnits, selectedUnitId]
    );

    const dosageStep = React.useMemo(() => getStepForUnit(selectedUnitName), [selectedUnitName]);

    const dosageMaxDecimals = React.useMemo(
        () => (selectedUnitName ? getMaxDecimalsForUnit(selectedUnitName) : undefined),
        [selectedUnitName]
    );

    const dosageError = React.useMemo<string | null>(() => {
        if (dosageAmount == null || !selectedUnitName || dosageMaxDecimals === undefined) {
            return null;
        }

        return isValidDecimalPlaces(dosageAmount, dosageMaxDecimals)
            ? null
            : `Maximum ${dosageMaxDecimals} decimal place${
                  dosageMaxDecimals === 1 ? '' : 's'
              } allowed for ${selectedUnitName}.`;
    }, [dosageAmount, selectedUnitName, dosageMaxDecimals]);

    const petOptions = React.useMemo(() => {
        return pets.reduce<Array<{ id: number; name: string; icon: IconDefinition; isRemovable: boolean }>>(
            (acc, pet) => {
                if (pet.deceased && !petIds?.includes(Number(pet.id))) {
                    return acc;
                }

                const petIcon =
                    pet.type?.id === PetTypeIds.DOGS ? faDog : pet.type?.id === PetTypeIds.CATS ? faCat : faTurtle;

                acc.push({
                    id: Number(pet.id),
                    name: pet.name,
                    icon: petIcon,
                    isRemovable: true
                });

                return acc;
            },
            []
        );
    }, [pets]);

    const selectedPetOptions = React.useMemo(() => {
        return petOptions
            .filter(pet => selectedPetIds.includes(pet.id))
            .map(pet => {
                if (petIds?.includes(pet.id)) {
                    return {
                        ...pet,
                        isRemovable: false
                    };
                }

                return pet;
            });
    }, [petOptions, selectedPetIds, petIds]);

    const activeStaff = React.useMemo<Array<Staff>>(() => {
        return staffMembers.filter(member => member.active && !member.deleted);
    }, [staffMembers]);

    const staffTagOptions = React.useMemo(
        () =>
            activeStaff.map(member => ({
                id: member.id ?? 0,
                name: `${member.person.firstName} ${member.person.lastName}`,
                image: member.person.avatar
            })),
        [activeStaff]
    );

    const selectedIncidentStaffOptions = React.useMemo(
        () => staffTagOptions.filter(staff => selectedIncidentStaffIds.includes(staff.id)),
        [staffTagOptions, selectedIncidentStaffIds]
    );

    const selectedIncidentSubtypeOptions = React.useMemo(
        () => incidentSubtypeOptions.filter(option => selectedIncidentSubtypeIds.includes(option.id)),
        [incidentSubtypeOptions, selectedIncidentSubtypeIds]
    );

    const selectedEnrichmentStaffOptions = React.useMemo(
        () => staffTagOptions.filter(staff => selectedEnrichmentStaffIds.includes(staff.id)),
        [staffTagOptions, selectedEnrichmentStaffIds]
    );

    const selectedEnrichmentSubtypeOptions = React.useMemo(
        () => enrichmentSubtypeOptions.filter(option => selectedEnrichmentSubtypeIds.includes(option.id)),
        [enrichmentSubtypeOptions, selectedEnrichmentSubtypeIds]
    );

    const isStaffSelectorLoading = React.useMemo(() => {
        return staffStatus === StaffStatus.Fetching && staffMembers.length === 0;
    }, [staffStatus, staffMembers.length]);

    const isValid = React.useMemo(() => {
        if (selectedType === ActivityType.PHOTO || selectedType === ActivityType.INCIDENT) {
            const validPhotos = photos.filter(photo => !photo.destroy);

            if (validPhotos.length > 10) {
                return false;
            }

            if (selectedType === ActivityType.PHOTO) {
                return validPhotos.length > 0;
            }
        }

        if (selectedType === ActivityType.VIDEO) {
            const validVideos = videos.filter(video => !video.destroy);

            return validVideos.length > 0 && validVideos.length <= 3;
        }

        if (selectedType === ActivityType.NOTE && note.length === 0) {
            return false;
        }

        if (selectedType === ActivityType.INCIDENT && selectedIncidentSubtypeIds.length === 0) {
            return false;
        }

        if (selectedType === ActivityType.ENRICHMENT && selectedEnrichmentSubtypeIds.length === 0) {
            return false;
        }

        if (selectedType === ActivityType.FEEDING && !selectedAte) {
            return false;
        }

        if (
            selectedType === ActivityType.MEDICATION &&
            (!medicationName.trim() || !selectedMethodId || !selectedUnitId || dosageError)
        ) {
            return false;
        }

        if (selectedType === ActivityType.HYDRATION) {
            return Boolean(hydrationIntake && selectedPetIds.length > 0 && selectedDate);
        }

        if (selectedType === ActivityType.REST) {
            return Boolean(restValue && selectedPetIds.length > 0 && selectedDate);
        }

        if (selectedType === ActivityType.RELIEFS) {
            const peedValid = peed.enabled && reliefCategorySelected(peed);
            const poopedValid = pooped.enabled && reliefCategorySelected(pooped);

            return Boolean((peedValid || poopedValid) && selectedPetIds.length > 0 && selectedDate);
        }

        return Boolean(selectedType && selectedPetIds.length > 0 && selectedDate);
    }, [
        selectedType,
        photos,
        videos,
        note,
        selectedPetIds,
        selectedDate,
        selectedIncidentSubtypeIds,
        selectedEnrichmentSubtypeIds,
        selectedAte,
        medicationName,
        selectedMethodId,
        selectedUnitId,
        dosageError,
        hydrationIntake,
        restValue,
        peed,
        pooped
    ]);

    const timeChangeHandler = React.useCallback(
        (value: string) => {
            const date = selectedDate || initialDate;
            const time = moment(value, 'hh:mma');

            setSelectedDate(
                date.clone().set({
                    hour: time.hour(),
                    minute: time.minute()
                })
            );
        },
        [selectedDate, initialDate]
    );

    const dateChangeHandler = React.useCallback(
        (value: Moment | null) => {
            if (!value) {
                return setSelectedDate(null);
            }

            const newDateTimeOptions = getTimeOptionsForDateRange(value, dateLimits, availableDateTimeRanges);

            const currentTime = (selectedDate || initialDate).format('hh:mma');

            const currentTimeValid = newDateTimeOptions.find(option => option.id === currentTime);

            if (newDateTimeOptions.length === 0) {
                setSelectedDate(value.clone());
                setIsDateSelectorExpanded(false);
                return;
            }

            let timeToUse: string;
            if (currentTimeValid) {
                timeToUse = currentTime;
            } else {
                timeToUse = newDateTimeOptions[0].id;
            }

            const timeToUseMoment = moment(timeToUse, 'hh:mma');
            setSelectedDate(
                value.clone().set({
                    hour: timeToUseMoment.hour(),
                    minute: timeToUseMoment.minute()
                })
            );
            setIsDateSelectorExpanded(false);
        },
        [selectedDate, initialDate, dateLimits, availableDateTimeRanges]
    );

    const resetFormHandler = React.useCallback(() => {
        setSelectedType(null);
        setSelectedPetIds(petIds ?? []);
        setSelectedDate(initialDate);
        setNote('');
        setPhotos([]);
        setVideos([]);
        setSelectedIncidentSubtypeIds([]);
        setSelectedIncidentStaffIds([]);
        setSelectedEnrichmentSubtypeIds([]);
        setSelectedEnrichmentStaffIds([]);
        setShared(false);
        setSelectedAte(null);
        setMedicationName('');
        setSelectedMethodId(null);
        setDosageAmount(0.5);
        setSelectedUnitId(null);
        setHydrationIntake(null);
        setRestValue(null);
        setPeed(emptyReliefCategory());
        setPooped(emptyReliefCategory());

        setSavedActivity(null);
    }, [petIds, initialDate]);

    const backHandler = React.useCallback(() => {
        if (!selectedType) {
            return;
        }

        resetFormHandler();
    }, [selectedType, resetFormHandler]);

    const submitHandler = React.useCallback(async () => {
        if (!selectedType || !selectedDate) {
            return;
        }

        let selectedSubtype: Array<string | ReliefBucket> = [];

        if (selectedType === ActivityType.INCIDENT) {
            selectedSubtype = selectedIncidentSubtypeIds;
        } else if (selectedType === ActivityType.ENRICHMENT) {
            selectedSubtype = selectedEnrichmentSubtypeIds;
        } else if (selectedType === ActivityType.HYDRATION && hydrationIntake) {
            selectedSubtype = [hydrationIntake];
        } else if (selectedType === ActivityType.REST && restValue) {
            selectedSubtype = [restValue];
        } else if (selectedType === ActivityType.RELIEFS) {
            selectedSubtype = [
                ...(peed.enabled ? [reliefBucketFromCategory(ReliefCategory.PEED, peed)] : []),
                ...(pooped.enabled ? [reliefBucketFromCategory(ReliefCategory.POOPED, pooped)] : [])
            ];
        }

        const selectedInvolvedStaffIds =
            selectedType === ActivityType.INCIDENT
                ? selectedIncidentStaffIds
                : selectedType === ActivityType.ENRICHMENT
                ? selectedEnrichmentStaffIds
                : [];

        const savedActivity = await dispatch(
            saveActivityThunk(apiClientWrapper, {
                uuid: uuid(),
                bookingId,
                marketplaceId: Number(marketplaceId),
                type: selectedType,
                date: selectedDate,
                pets: [],
                petIds: selectedPetIds,
                activitySubtype: selectedSubtype,
                involvedStaffIds: selectedInvolvedStaffIds,
                note,
                sharedAt: shared ? moment() : null,
                ate: selectedType === ActivityType.FEEDING ? selectedAte ?? undefined : undefined,
                medicationName: selectedType === ActivityType.MEDICATION ? medicationName : undefined,
                medicationMethodId:
                    selectedType === ActivityType.MEDICATION ? selectedMethodId ?? undefined : undefined,
                dosageAmount: selectedType === ActivityType.MEDICATION ? dosageAmount : undefined,
                dosageUnitId: selectedType === ActivityType.MEDICATION ? selectedUnitId ?? undefined : undefined,
                photos: photos.map(photo => ({
                    id: photo.id,
                    url: photo.url,
                    file: photo.fileToUpload as File,
                    destroy: photo.destroy
                })),
                videos: videos.map(video => ({
                    id: video.id,
                    url: video.url,
                    file: video.fileToUpload as File,
                    destroy: video.destroy
                })),

                ...(activityToEdit && {
                    id: activityToEdit.id,
                    uuid: activityToEdit.uuid
                })
            })
        ).catch(() => {
            setError('Failed to save activity, please try again.');
        });

        if (!savedActivity) {
            return;
        }

        setSavedActivity(savedActivity);
    }, [
        activityToEdit,
        apiClientWrapper,
        bookingId,
        marketplaceId,
        selectedType,
        selectedPetIds,
        selectedDate,
        selectedIncidentSubtypeIds,
        selectedIncidentStaffIds,
        selectedEnrichmentSubtypeIds,
        selectedEnrichmentStaffIds,
        photos,
        videos,
        note,
        shared,
        selectedAte,
        medicationName,
        selectedMethodId,
        dosageAmount,
        selectedUnitId,
        hydrationIntake,
        restValue,
        peed,
        pooped
    ]);

    React.useEffect(() => {
        const requiresStaffSelector =
            selectedType === ActivityType.INCIDENT || selectedType === ActivityType.ENRICHMENT;

        if (!requiresStaffSelector || staffMembers.length > 0 || staffStatus === StaffStatus.Fetching) {
            return;
        }

        dispatch(fetchStaffThunk());
    }, [selectedType, staffMembers.length, staffStatus]);

    React.useEffect(() => {
        setError(null);
    }, [
        selectedPetIds,
        selectedDate,
        selectedType,
        photos,
        videos,
        note,
        selectedIncidentSubtypeIds,
        selectedIncidentStaffIds,
        selectedEnrichmentSubtypeIds,
        selectedEnrichmentStaffIds,
        selectedAte,
        medicationName,
        selectedMethodId,
        dosageAmount,
        selectedUnitId,
        hydrationIntake,
        restValue,
        peed,
        pooped
    ]);

    return (
        <Drawer
            zIndex={zIndex}
            onClose={() => onClose?.(savedActivity ?? undefined)}
        >
            <DrawerHeader
                title={title}
                className={styles.header}
                onBack={activityToEdit || !selectedType || savedActivity ? undefined : backHandler}
            />

            {!selectedType && (
                <DrawerBody className={styles.typeSelector}>
                    {ACTIVITY_CREATE_OPTIONS.map(option => (
                        <IconCard
                            key={option.id}
                            title={option.label}
                            icon={option.icon}
                            backgroundColor={ACTIVITY_FORM_CARD_COLORS_MAP[option.id]}
                            onClick={() => setSelectedType(option.id)}
                        />
                    ))}
                </DrawerBody>
            )}

            {selectedType && !savedActivity && (
                <DrawerBody className={styles.body}>
                    {error && (
                        <AlertMessage
                            icon={faCircleExclamation}
                            bdColor="#EF4F57"
                            bgColor="#FBB9BC33"
                            iconColor="#EF4F57"
                        >
                            {error}
                        </AlertMessage>
                    )}

                    <TagsSelector
                        title="Pets"
                        direction="down"
                        addButtonColor="black"
                        disabled={!!activityToEdit}
                        tags={petOptions}
                        selected={selectedPetOptions}
                        onSelect={id => setSelectedPetIds([...selectedPetIds, id])}
                        onSelectAll={petIds => setSelectedPetIds([...selectedPetIds, ...petIds])}
                        onRemove={removeId => setSelectedPetIds(selectedPetIds.filter(id => id !== removeId))}
                    />

                    <div className={styles.formGroupGrid}>
                        <ExpandableSelector
                            title="Date"
                            id="date-selector"
                            disabled={!!activityToEdit}
                            expanded={isDateSelectorExpanded}
                            childrenClassName={styles.dateSelector}
                            label={selectedDate?.format('MMM, DD YYYY')}
                            onExpand={() => setIsDateSelectorExpanded(!isDateSelectorExpanded)}
                            onClickAway={() => setIsDateSelectorExpanded(false)}
                        >
                            <DatePicker
                                minDate={dateLimits?.from || fallbackMinDate}
                                maxDate={dateLimits?.to || fallbackMaxDate}
                                allowedDates={allowedDates}
                                timeZone={timeZone}
                                value={selectedDate}
                                onChange={dateChangeHandler}
                            />
                        </ExpandableSelector>

                        {selectedType !== ActivityType.RELIEFS && (
                            <TimeSelector
                                label="Time"
                                value={timeOptions.find(option => option.id === selectedDate?.format('hh:mma'))}
                                options={timeOptions}
                                disabled={!!activityToEdit}
                                onChange={timeChangeHandler}
                            />
                        )}
                    </div>

                    {selectedType === ActivityType.INCIDENT && (
                        <>
                            <MultiSelectField
                                label="Incident Type"
                                placeholder="Select Incident"
                                options={incidentSubtypeOptions}
                                value={selectedIncidentSubtypeOptions}
                                zIndex={zIndex !== undefined ? zIndex + 1 : 10011}
                                onChange={options => setSelectedIncidentSubtypeIds(options.map(option => option.id))}
                            />

                            <TagsSelector
                                title="Staff Involved"
                                direction="down"
                                addButtonColor="black"
                                selectAllLabel="All Members"
                                selectAllIcon={faUsers}
                                tags={staffTagOptions}
                                selected={selectedIncidentStaffOptions}
                                loading={isStaffSelectorLoading}
                                onSelect={staffId =>
                                    setSelectedIncidentStaffIds(prev =>
                                        prev.includes(staffId) ? prev : [...prev, staffId]
                                    )
                                }
                                onSelectAll={ids => setSelectedIncidentStaffIds(Array.from(new Set(ids)))}
                                onRemove={staffId =>
                                    setSelectedIncidentStaffIds(prev => prev.filter(id => id !== staffId))
                                }
                            />
                        </>
                    )}

                    {selectedType === ActivityType.ENRICHMENT && (
                        <>
                            <MultiSelectField
                                label="Activity Type"
                                placeholder="Select Activity"
                                options={enrichmentSubtypeOptions}
                                value={selectedEnrichmentSubtypeOptions}
                                zIndex={zIndex !== undefined ? zIndex + 1 : 10011}
                                onChange={options => setSelectedEnrichmentSubtypeIds(options.map(option => option.id))}
                            />

                            <TagsSelector
                                title="Staff"
                                direction="down"
                                addButtonColor="black"
                                selectAllLabel="All Members"
                                selectAllIcon={faUsers}
                                tags={staffTagOptions}
                                selected={selectedEnrichmentStaffOptions}
                                loading={isStaffSelectorLoading}
                                onSelect={staffId =>
                                    setSelectedEnrichmentStaffIds(prev =>
                                        prev.includes(staffId) ? prev : [...prev, staffId]
                                    )
                                }
                                onSelectAll={ids => setSelectedEnrichmentStaffIds(Array.from(new Set(ids)))}
                                onRemove={staffId =>
                                    setSelectedEnrichmentStaffIds(prev => prev.filter(id => id !== staffId))
                                }
                            />
                        </>
                    )}

                    {selectedType === ActivityType.FEEDING && (
                        <RadioGroup
                            label="Ate"
                            value={selectedAte as FeedingAte}
                            options={(feedingAteValues ?? []).map(v => ({
                                value: v.id as FeedingAte,
                                inlineLabel: v.name
                            }))}
                            onChange={v => setSelectedAte(v)}
                        />
                    )}

                    {selectedType === ActivityType.MEDICATION && (
                        <>
                            <Input
                                label="Medication Name"
                                placeholder="Medication Name"
                                value={medicationName}
                                autoFocus={false}
                                onChange={e => setMedicationName(e.target.value)}
                            />
                            <div className={styles.methodDosageRow}>
                                <Select
                                    label="Method"
                                    placeholder="Select Method"
                                    options={medicationMethodOptions}
                                    value={selectedMethodId ? String(selectedMethodId) : undefined}
                                    onChange={val => setSelectedMethodId(Number(val))}
                                />
                                <div className={styles.dosageField}>
                                    <Label>Dosage</Label>
                                    <div className={styles.dosagePill}>
                                        <EditableCounterField
                                            className={styles.dosageCounter}
                                            value={dosageAmount}
                                            minLimit={0}
                                            step={dosageStep}
                                            onChange={setDosageAmount}
                                        />
                                        <div className={styles.dosageDivider} />
                                        <Dropdown>
                                            <DropdownTrigger>
                                                <div className={styles.unitTrigger}>
                                                    <span>
                                                        {medicationUnitOptions.find(
                                                            o => o.value === String(selectedUnitId)
                                                        )?.label ?? 'Unit'}
                                                    </span>
                                                    <FontAwesomeIcon icon={faChevronDown} />
                                                </div>
                                            </DropdownTrigger>
                                            <DropdownContent
                                                className={styles.unitDropdown}
                                                align="end"
                                            >
                                                {medicationUnitOptions.map(opt => (
                                                    <DropdownItem
                                                        key={opt.value}
                                                        checked={opt.value === String(selectedUnitId)}
                                                        onSelect={() => setSelectedUnitId(Number(opt.value))}
                                                    >
                                                        {opt.label}
                                                    </DropdownItem>
                                                ))}
                                            </DropdownContent>
                                        </Dropdown>
                                    </div>
                                    {dosageError && (
                                        <p
                                            className={styles.dosageError}
                                            role="alert"
                                        >
                                            {dosageError}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {selectedType === ActivityType.HYDRATION && (
                        <RadioGroup<HydrationIntake | null>
                            label="Water Intake"
                            className={styles.radioRow}
                            value={hydrationIntake}
                            options={HYDRATION_OPTIONS}
                            onChange={setHydrationIntake}
                        />
                    )}

                    {selectedType === ActivityType.REST && (
                        <RadioGroup<RestValue | null>
                            label="Have they rested?"
                            className={styles.radioRow}
                            value={restValue}
                            options={REST_OPTIONS}
                            onChange={setRestValue}
                        />
                    )}

                    {selectedType === ActivityType.RELIEFS && (
                        <>
                            <ReliefSection
                                title="Peed"
                                noteLabel="Pee Notes"
                                value={peed}
                                onChange={setPeed}
                            />

                            <ReliefSection
                                title="Pooped"
                                noteLabel="Poo Notes"
                                value={pooped}
                                onChange={setPooped}
                            />
                        </>
                    )}

                    {[ActivityType.PHOTO, ActivityType.INCIDENT, ActivityType.ENRICHMENT].includes(selectedType) && (
                        <PhotosSelector
                            size="small"
                            title="Photos"
                            photos={photos}
                            multipleSelection
                            onChange={setPhotos}
                        />
                    )}

                    {selectedType === ActivityType.VIDEO && (
                        <VideosSelector
                            size="small"
                            title="Videos"
                            videos={videos}
                            multipleSelection
                            onChange={setVideos}
                        />
                    )}

                    {selectedType !== ActivityType.RELIEFS && (
                        <Textarea
                            value={note}
                            maxLength={500}
                            label={
                                selectedType === ActivityType.INCIDENT
                                    ? 'Incident Description'
                                    : selectedType === ActivityType.FEEDING
                                    ? 'Feeding Notes'
                                    : selectedType === ActivityType.MEDICATION
                                    ? 'Medication Notes'
                                    : selectedType === ActivityType.HYDRATION
                                    ? 'Hydration Notes'
                                    : selectedType === ActivityType.REST
                                    ? 'Rest Notes'
                                    : 'Activity Notes'
                            }
                            placeholder="Notes here..."
                            onChange={e => setNote(e.target.value)}
                        />
                    )}

                    {customerName && (
                        <Checkbox
                            checked={shared}
                            label={`Share activity with ${customerName}`}
                            onChange={setShared}
                        />
                    )}
                </DrawerBody>
            )}

            {savedActivity && (
                <SuccessScreen
                    title="Good Job"
                    message={activityToEdit ? 'Your activity has been updated.' : 'Your activity has been added.'}
                    buttonLabel={activityToEdit ? 'Add Activity' : 'Add Another Activity'}
                    buttonVariant="green"
                    onClick={resetFormHandler}
                />
            )}

            {(selectedType || savedActivity) && (
                <DrawerFooter>
                    {!savedActivity ? (
                        <Button
                            label="Save"
                            size="large"
                            variant="green"
                            disabled={!isValid}
                            isLoading={isLoading}
                            onClick={submitHandler}
                        />
                    ) : (
                        <Button
                            label="Done"
                            size="large"
                            variant="black"
                            onClick={() => onClose?.(savedActivity)}
                        />
                    )}
                </DrawerFooter>
            )}
        </Drawer>
    );
};
